/**
 * @bos/portal3 — the Documents section of a project page (M5).
 *
 * An upload control (accept=".doc,.docx,.pdf,.md"), a list of the project's documents
 * (name / type / size / date) with loading / empty / error states, an authorized Download
 * action (a same-origin <a href> — the httpOnly cookie rides automatically), and a Delete
 * action. Validation errors from the server (wrong type / too big) are shown cleanly.
 */
import { useRef, useState, type ChangeEvent } from 'react';
import {
  useDocuments,
  documentErrorMessage,
  downloadUrl,
  type DocumentSummary,
} from './hooks';

const KIND_LABEL: Record<DocumentSummary['kind'], string> = {
  doc: 'Document',
  pdf: 'PDF',
  md: 'Markdown',
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function DocumentRow({
  doc,
  onDelete,
}: {
  doc: DocumentSummary;
  onDelete: (id: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onDeleteClick() {
    setError(null);
    setBusy(true);
    try {
      await onDelete(doc.id);
    } catch (err) {
      setError(documentErrorMessage(err));
      setBusy(false); // stay mounted to show the error; on success the row unmounts
    }
  }

  return (
    <li className="doc-card">
      <div className="doc-card__main">
        <span className="doc-card__name" title={doc.filename}>
          {doc.filename}
        </span>
        <span className="doc-card__meta">
          <span className="badge badge--kind">{KIND_LABEL[doc.kind]}</span>
          <span className="doc-card__size">{formatBytes(doc.sizeBytes)}</span>
          <span className="doc-card__date">{formatDate(doc.createdAt)}</span>
        </span>
        {error && (
          <span className="form__error doc-card__error" role="alert">
            {error}
          </span>
        )}
      </div>
      <div className="doc-card__actions">
        {/* Same-origin authorized endpoint: the cookie is attached by the browser. */}
        <a className="btn btn--ghost btn--sm" href={downloadUrl(doc.id)}>
          Download
        </a>
        <button
          className="btn btn--ghost btn--sm btn--danger"
          type="button"
          onClick={() => void onDeleteClick()}
          disabled={busy}
        >
          {busy ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </li>
  );
}

export function DocumentsSection({ projectId }: { projectId: string }) {
  const { documents, status, error, reload, upload, remove } = useDocuments(projectId);

  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUploadBusy(true);
    try {
      await upload(file);
    } catch (err) {
      setUploadError(documentErrorMessage(err));
    } finally {
      setUploadBusy(false);
      // Reset so re-selecting the SAME file fires onChange again.
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <section className="documents" aria-label="Documents">
      <h2 className="form__heading">Documents</h2>
      <p className="form__note">
        Upload the business’s own documents (Word, PDF, or Markdown, up to 25&nbsp;MB). They
        become the raw material this project is built from.
      </p>
      {/* Said BEFORE the upload, not after a build: the two formats Sorvel genuinely
          cannot read are named here so nobody discovers it from a held-back notice.
          There is no OCR anywhere in Sorvel and this sentence does not pretend there is. */}
      <p className="form__note documents__reads">
        Sorvel reads the text of <strong>.docx</strong>, <strong>.pdf</strong> and{' '}
        <strong>.md</strong> files. It cannot read the older <strong>.doc</strong> format, or a
        PDF that is only scanned images — those upload and store fine, but a build will tell you
        it had to hold them back.
      </p>

      <div className="documents__upload">
        <label className={`btn btn--primary${uploadBusy ? ' btn--disabled' : ''}`}>
          {uploadBusy ? 'Uploading…' : 'Upload a document'}
          <input
            ref={inputRef}
            className="documents__file-input"
            type="file"
            accept=".doc,.docx,.pdf,.md"
            onChange={(e) => void onFileChange(e)}
            disabled={uploadBusy}
          />
        </label>
      </div>

      {uploadError && (
        <p className="form__error" role="alert">
          {uploadError}
        </p>
      )}

      {status === 'loading' && (
        <p className="page__note" aria-busy="true">
          Loading documents…
        </p>
      )}

      {status === 'error' && (
        <div className="projects__state">
          <p className="form__error" role="alert">
            {error ?? 'Could not load documents.'}
          </p>
          <button className="btn btn--ghost" type="button" onClick={() => void reload()}>
            Try again
          </button>
        </div>
      )}

      {status === 'ready' && documents.length === 0 && (
        <p className="page__note">No documents yet. Upload your first one above.</p>
      )}

      {status === 'ready' && documents.length > 0 && (
        <ul className="doc-list">
          {documents.map((doc) => (
            <DocumentRow key={doc.id} doc={doc} onDelete={remove} />
          ))}
        </ul>
      )}
    </section>
  );
}
